import { FC, useEffect, useMemo, useState } from 'react';
import { Box, Chip, Divider, styled, Typography } from '@mui/material';
import { formatCurrency } from '../formattingUtils';
import { ApprovingLenderLogo } from '../pages/main';
import {
  ConsumerAccountData,
  useGetConsumerAccounts,
  useGetConsumerId,
  useGetViewedConsumerRowDetails,
  useSetViewedConsumerRowDetails,
  ConsumerRowDetails,
  Status,
  useGetAccountData,
  useGetValidAccounts,
  useSetConsumersListRefresher,
} from '../services/customersTabManager';
import {
  useSetApprovedAmount,
  useSetApprovingLender,
  useSetIsApprovedAmountEstimated,
  useSetPrequalifyConfirmationToken,
  useSetSelectedTab,
  useApplyStartOver,
  useCheckout,
  useCreateCharge,
  useSetCheckoutMinimumAmount,
  useSetFinancialProductType,
  useSetConsumer,
} from '../services/flowManager';
import { VerticalSpace } from '@chargeafter/ui';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import { useGetMerchantId } from '../services/configManager';
import { CheckMarkIcon, OutlinedManIcon, OutlinedPhoneIcon } from '../icons';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { saveToUserSession } from '../services/userSessionStorageManager';
import { addToContext, removeFromContext } from '../services/contextManager';
import { ButtonName, SELF_FINANCING_PRODUCT } from '../constants';
import { ColumnMeta, DataGrid } from '@chargeafter/data-grid';
import logger from '../services/loggerManager';
import { useSecureRestGrid } from '../restClient';
import { useGetColumnsREST } from '../services/tableManager';
import { MuiTrackerButton } from '../components/TrackerButtonWrapper';
import { useGetSelectedLenderDoesNotSupportOtb } from '../services/lendersConfigurationManager';
import { useAuth0 } from '@auth0/auth0-react';
import { startFsRecording } from '../services/fullStoryManager';
import StatusChip from '../components/StatusChip';
import { tracker } from '../services/trackerManager';
import { useResetCurrentModal } from '../services/modalManager';
import { NoData } from '../components/NoData';

const StyledModalBox = styled(Box)({
  minWidth: '93vw',
  minHeight: 538,
  maxHeight: 'calc(60vh + 50px + 72px)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
});

type ConsumerAccountsModalProps = {
  close: () => void;
  updateClose: (func: () => void) => void;
};

const useUpdateInitialConsumerData = () => {
  const { user } = useAuth0();

  return (consumerData: ConsumerRowDetails) => {
    const consumerIdentificationData =
      consumerData?.mobilePhoneNumber ?? consumerData?.id;
    saveToUserSession('consumerDetails', consumerData);
    addToContext('consumer', consumerData);
    startFsRecording(user?.email ?? '', consumerIdentificationData);
    tracker.identify(consumerIdentificationData!);
  };
};

const ConsumerAccountsModal: FC<ConsumerAccountsModalProps> = ({
  updateClose,
  close,
}) => {
  const [loading, setLoading] = useState(false);
  const stopLoading = () => setLoading(false);
  const consumerRowDetails = useGetViewedConsumerRowDetails();
  const checkout = useCheckout(stopLoading);
  const createCharge = useCreateCharge();
  const updateConsumer = useSetConsumer();
  const updateSelectedTab = useSetSelectedTab();
  const applyStartOver = useApplyStartOver();
  const updateLender = useSetApprovingLender();
  const updateApprovedAmount = useSetApprovedAmount();
  const updateMinimumAmount = useSetCheckoutMinimumAmount();
  const updateFinancialProductType = useSetFinancialProductType();
  const updateIsAmountEstimated = useSetIsApprovedAmountEstimated();
  const updateConsumerRowDetails = useSetViewedConsumerRowDetails();
  const updatePrequalifyConfirmationToken = useSetPrequalifyConfirmationToken();
  const validAccounts = useGetValidAccounts();
  const updateConsumersTable = useSetConsumersListRefresher();
  const resetCurrentModal = useResetCurrentModal();

  const latestLenderId = (useGetAccountData('lenderId') as string) ?? '';
  const latestLenderDoesNotSupportOtb =
    useGetSelectedLenderDoesNotSupportOtb(latestLenderId);
  const latestApprovedAmount =
    (useGetAccountData('availableAmount') as number) ?? 0;

  const latestMinimumAmount =
    (useGetAccountData('checkoutMinimumAmount') as number) ?? null;

  const latestFinancialProductType =
    (useGetAccountData('financialProductType') as string) ?? null;

  const latestIsAmountEstimated = useGetAccountData('estimated') as boolean;
  const latestApplicationId = useGetAccountData('applicationId') as string;

  const latestConsumerStatus =
    (useGetAccountData('status') as Status) ?? 'Prequalified';

  const updateInitialConsumerData = useUpdateInitialConsumerData();

  const handleButtonClick = async () => {
    switch (latestConsumerStatus) {
      case 'PendingConsumer':
      case 'CheckoutInProgress':
        close();
        if (consumerRowDetails) addToContext('consumer', consumerRowDetails);
        addToContext('applicationId', latestApplicationId);
        const checkoutResult = await checkout(
          latestApplicationId,
          false,
          latestLenderId,
        );

        if (checkoutResult && checkoutResult.token) {
          close();
          updateConsumerRowDetails(null);
          updateSelectedTab('customers');
          await createCharge(
            checkoutResult.settlementOption,
            checkoutResult.token,
            checkoutResult.lender.id,
            true,
          );
        }
        break;
      default:
        await handleConsumerSelection();
    }
  };

  const handleConsumerSelection = async () => {
    try {
      removeFromContext('consumer');
      setLoading(true);
      applyStartOver(false, true);
      updateLender(latestLenderId);
      saveToUserSession('lenderId', latestLenderId);

      updateFinancialProductType(latestFinancialProductType);
      saveToUserSession('financialProductType', latestFinancialProductType);

      const approvedAmountByProduct =
        latestFinancialProductType !== SELF_FINANCING_PRODUCT &&
        !latestLenderDoesNotSupportOtb
          ? latestApprovedAmount
          : 0;

      updateApprovedAmount(approvedAmountByProduct);
      saveToUserSession('creditAmount', approvedAmountByProduct);

      updateIsAmountEstimated(latestIsAmountEstimated);
      saveToUserSession('isApprovedAmountEstimated', latestIsAmountEstimated);

      updateMinimumAmount(latestMinimumAmount);
      saveToUserSession('checkoutMinimumAmount', latestMinimumAmount);

      updatePrequalifyConfirmationToken(undefined);
      saveToUserSession('applicationId', undefined);

      updateConsumer({ ...consumerRowDetails });
      updateInitialConsumerData(consumerRowDetails!);
      close();
      updateSelectedTab('cart');
      updateConsumerRowDetails(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    updateClose(() => () => {
      resetCurrentModal();
      logger.log(`Closed consumer account modal`, {
        filename: 'ConsumerAccountsModal.tsx',
        function: 'closeModal',
      });
      removeFromContext('consumer');
      updateConsumersTable(Math.random().toString());
    });
  }, []);

  const buttonText = buttonTextByStatus(latestConsumerStatus);

  return (
    <StyledModalBox>
      <Box>
        {consumerRowDetails && (
          <ConsumerDetailsHeader consumerRowDetails={consumerRowDetails} />
        )}
        <ConsumerAccounts />
      </Box>
      {validAccounts &&
        validAccounts.length > 0 &&
        latestConsumerStatus !== 'PendingLender' && (
          <MuiTrackerButton
            buttonName={
              buttonText === 'continue'
                ? ButtonName.ContinuePendingConsumer
                : ButtonName.SelectConsumer
            }
            data-testid="consumer-account-modal-button"
            variant="contained"
            disabled={loading}
            fullWidth
            onClick={handleButtonClick}
            loading={loading}
            loaderColor="inherit"
          >
            <Box display="flex" gap={2}>
              <ButtonIconByStatus status={latestConsumerStatus} />
              <Typography fontWeight={700}>{buttonText}</Typography>
            </Box>
          </MuiTrackerButton>
        )}
    </StyledModalBox>
  );
};

export default ConsumerAccountsModal;

const StyledAccountsBox = styled(Box)({
  maxHeight: '60vh',
  overflowY: 'auto',
  padding: '48px 48px 0 48px',
});

const StyledEmptyAccountsBox = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: 'calc(538px - 48px - 40px - 72px)', //538 - modal, 48-padding 40-button  72-consumer details header
});

type ConsumerAccountsProps = {};
const ConsumerAccounts: FC<ConsumerAccountsProps> = () => {
  const accounts: ConsumerAccountData[] | undefined = useGetConsumerAccounts();

  return (
    <StyledAccountsBox>
      {accounts?.map((account, idx) => {
        return (
          <Box key={account.lenderId}>
            <>
              <AccountItem
                lenderId={account.lenderId}
                originalCredit={account.originalAmount}
                availableCredit={account.availableAmount ?? null}
                financialProductType={account.financialProductType ?? null}
                estimated={account.estimated}
              />
              <VerticalSpace units={6} />
              <TransactionsTable lenderId={account.lenderId} />
            </>
            {idx !== accounts.length - 1 && <Divider sx={{ my: 5 }} />}
          </Box>
        );
      })}
      {!accounts?.length && (
        <StyledEmptyAccountsBox>
          <Typography>No history found</Typography>
        </StyledEmptyAccountsBox>
      )}
    </StyledAccountsBox>
  );
};

const StyledDetailsBox = styled(Box)(({ theme }) => ({
  border: `2px solid ${theme.palette.grey[200]}`,
  borderRadius: 2,
  borderWidth: 2,
  display: 'flex',
  width: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  '& > div': {
    width: '100%',
    padding: 10,
    textAlign: 'center',
  },
  '& > div:not(:last-child)': {
    borderRight: `2px solid ${theme.palette.grey[200]}`,
  },
}));

const StyledEstimatedChip = styled(Chip)({
  position: 'absolute',
  left: 'calc(50% - 44px)',
  top: 'calc(100% - 8px)',
});

type AccountItemProps = {
  lenderId: string;
  originalCredit: number;
  availableCredit: number | null;
  financialProductType?: string | null;
  estimated: boolean;
};

const AccountItem: FC<AccountItemProps> = ({
  lenderId,
  originalCredit,
  availableCredit,
  financialProductType,
  estimated,
}) => {
  const lenderDoesNotSupportOtb =
    useGetSelectedLenderDoesNotSupportOtb(lenderId);
  const displayAvailableCreditValue =
    availableCredit && availableCredit > 0
      ? formatCurrency(availableCredit)
      : 'N/A';
  return (
    <StyledDetailsBox>
      <Box>
        <ApprovingLenderLogo lenderId={lenderId} />
      </Box>
      {!lenderDoesNotSupportOtb &&
        financialProductType !== SELF_FINANCING_PRODUCT && (
          <>
            <Box>
              <Typography variant="body2">Original Amount:</Typography>
              <Typography
                variant="h2"
                color="grey.400"
                data-testid="original-credit"
              >
                {formatCurrency(originalCredit)}
              </Typography>
            </Box>

            <Box position="relative">
              <Typography variant="body2">Available Amount:</Typography>
              <Typography
                variant="h2"
                color="primary"
                data-testid="available-credit"
              >
                {displayAvailableCreditValue}
              </Typography>
              {estimated && (
                <StyledEstimatedChip variant="grey" label="Estimated" />
              )}
            </Box>
          </>
        )}
    </StyledDetailsBox>
  );
};

const StyledDataGridBox = styled(Box)({
  height: 250,
});

type TransactionsTableProps = { lenderId: string };
const TransactionsTable: FC<TransactionsTableProps> = ({ lenderId }) => {
  const merchantId = useGetMerchantId();
  const consumerId = useGetConsumerId();
  const { columns, loadServerRows } = useSecureRestGrid(
    'consumers/accounts-orders-data',
    `consumers/${consumerId}/merchants/${merchantId}/lenders/${lenderId}/orders`,
    useGetColumnsREST,
  );

  const newColumns: ColumnMeta[] = useMemo(() => {
    return columns.map((col: ColumnMeta) => {
      const newColumn = { ...col };
      newColumn.minWidth = 100;
      if (col.field === 'status') {
        newColumn.minWidth = 160;
        newColumn.renderCell = (params: GridRenderCellParams) => {
          const { value } = params;
          return <StatusChip status={value} />;
        };
      } else if (col.field === 'offerDescription') {
        newColumn.renderCell = (params: GridRenderCellParams) => {
          const { value } = params;
          return (
            <Box
              sx={{
                overflow: 'auto',
              }}
            >
              {value}
            </Box>
          );
        };
      }
      return newColumn;
    });
  }, [columns]);

  return (
    <StyledDataGridBox>
      <DataGrid
        gridPreLoader={false}
        initialState={{
          sorting: {
            sortModel: [{ field: 'updated', sort: 'desc' }],
          },
        }}
        noDataComponent={<NoData type="transaction" />}
        columns={newColumns}
        loadServerRows={loadServerRows}
        toolbar={null}
      />
    </StyledDataGridBox>
  );
};

const StyledHeaderBox = styled(Box)(({ theme }) => ({
  height: 72,
  backgroundColor: theme.palette.secondary.light,
  display: 'flex',
  justifyContent: 'space-evenly',
  alignItems: 'center',
  color: 'black',
  svg: {
    color: theme.palette.primary.main,
  },
}));

const StyledConsumerDetail = styled(Box)({
  display: 'flex',
  gap: 10,
});

type ConsumerDetailsHeaderProps = {
  consumerRowDetails: ConsumerRowDetails;
};
const ConsumerDetailsHeader: FC<ConsumerDetailsHeaderProps> = ({
  consumerRowDetails,
}) => {
  return (
    <StyledHeaderBox>
      <StyledConsumerDetail>
        <OutlinedManIcon fontSize="small" />
        <Typography variant="body2">
          {consumerRowDetails.firstName} {consumerRowDetails.lastName}
        </Typography>
      </StyledConsumerDetail>
      <StyledConsumerDetail>
        <AlternateEmailIcon fontSize="small" />
        <Typography variant="body2">{consumerRowDetails.email}</Typography>
      </StyledConsumerDetail>
      {consumerRowDetails.mobilePhoneNumber && (
        <StyledConsumerDetail>
          <OutlinedPhoneIcon fontSize="small" />
          <Typography variant="body2">
            {consumerRowDetails.mobilePhoneNumber}
          </Typography>
        </StyledConsumerDetail>
      )}
    </StyledHeaderBox>
  );
};

const buttonTextByStatus = (status: Status) => {
  switch (status) {
    case 'CheckoutInProgress':
    case 'PendingConsumer':
      return 'continue';
    default:
      return 'select customer';
  }
};

type ByStatusProps = { status: Status };

const ButtonIconByStatus: FC<ByStatusProps> = ({ status }) => {
  switch (status) {
    case 'CheckoutInProgress':
    case 'PendingConsumer':
      return <FileUploadOutlinedIcon />;
    default:
      return <CheckMarkIcon />;
  }
};
